import React, { memo, ReactNode } from 'react';
import { BasePage } from '~/lib/data-contract';
import { Link } from '~/shared/components';
import { useTranslation } from '~/shared/utils';
import { ConditionalWrap, weakKey } from '~/shared/utils/jsx';
import { usePage } from '~/templates/pages';
import {
    StyledBreadcrumb,
    StyledBreadcrumbContainer,
    StyledBreadcrumbText,
    StyledNavigation,
} from './styled';

/**
 * Displays the current pages breadcrumb.
 * For now it also handles logic on when to show and when not to show.
 */
export const N50Breadcrumb = memo(({ children }: { children?: ReactNode }) => {
    const { breadcrumb, type = '' } = usePage<BasePage>();
    const { items } = breadcrumb || {};
    const { translate } = useTranslation();

    // Add pages that should not include the last item
    const hideActive = ['p30CategoryPage'].includes(type);

    // Add pages that should not include the breadcrumb
    if (['p20FrontPage', 'p50ProductDetailPage'].includes(type)) {
        return null;
    }

    const activeIndex = (items?.length || 1) - 1;

    return (
        <StyledNavigation aria-label="breadcrumb">
            <StyledBreadcrumbContainer>
                {items?.map((link, index) => {
                    const key = weakKey(link);
                    const isActive = index === activeIndex;
                    const isLast = index === items.length - 1;
                    const isHidden = isActive && hideActive;

                    if (isHidden) {
                        return null;
                    }

                    return (
                        <StyledBreadcrumb
                            key={key}
                            // location is prefered over page
                            // @see https://www.aditus.io/aria/aria-current/#aria-current-location
                            aria-current={isActive ? 'location' : undefined}
                        >
                            <ConditionalWrap
                                if={Boolean(link.url && !isLast)}
                                wrap={(child) => (
                                    <Link
                                        key={link.url}
                                        prefetch={false}
                                        href={link.url || ''}
                                        type="router"
                                    >
                                        {child}
                                    </Link>
                                )}
                            >
                                <StyledBreadcrumbText as="span" variant="caption" isLast={isLast}>
                                    {link.text ? link.text : translate('commerce.all')}
                                </StyledBreadcrumbText>
                            </ConditionalWrap>
                        </StyledBreadcrumb>
                    );
                })}
            </StyledBreadcrumbContainer>
            {children}
        </StyledNavigation>
    );
});
